// App version - single source of truth
export const APP_VERSION = '2.4.8';

// Base colors - 12 distinct, high-contrast colors
export const USER_BASE_COLORS = [
  { hex: '#2563EB', name: 'Blue' },       // Vibrant blue
  { hex: '#DC2626', name: 'Red' },        // True red
  { hex: '#16A34A', name: 'Green' },      // Forest green
  { hex: '#EA580C', name: 'Orange' },     // Burnt orange
  { hex: '#7C3AED', name: 'Purple' },     // Deep purple
  { hex: '#DB2777', name: 'Pink' },       // Hot pink
  { hex: '#0891B2', name: 'Cyan' },       // Ocean cyan
  { hex: '#CA8A04', name: 'Gold' },       // Golden yellow
  { hex: '#4F46E5', name: 'Indigo' },     // Rich indigo
  { hex: '#059669', name: 'Teal' },       // Deep teal
  { hex: '#92400E', name: 'Brown' },      // Chocolate brown
  { hex: '#64748B', name: 'Slate' },      // Cool slate
] as const;

// Pattern types
export const USER_PATTERNS = [
  { id: 'solid', name: 'Solid' },
  { id: 'stripes', name: 'Stripes' },
  { id: 'dots', name: 'Dots' },
  { id: 'diagonal', name: 'Diagonal' },
] as const;

// Generate all color+pattern combinations (48 total)
// Order by pattern first so each new user gets a different base color
// before patterns start repeating (Blue-solid, Red-solid, ... then Blue-stripes, Red-stripes, ...)
export const USER_COLORS = USER_PATTERNS.flatMap(pattern =>
  USER_BASE_COLORS.map(color => ({
    hex: color.hex,
    pattern: pattern.id as 'solid' | 'stripes' | 'dots' | 'diagonal',
    name: pattern.id === 'solid' ? color.name : `${color.name} ${pattern.name}`,
  }))
);

// Reserved colors
export const OTHER_USER_COLOR = '#9CA3AF'; // Gray for other users' bookings (User view)
export const PENDING_BACKGROUND = '#FEF3C7'; // Yellow for pending status

// Session durations
export const SESSION_DURATION_DEFAULT = '7d';
export const SESSION_DURATION_EXTENDED = '30d';

// Token expiration times (in milliseconds)
export const INVITATION_EXPIRY_MS = 72 * 60 * 60 * 1000; // 72 hours
export const PASSWORD_RESET_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours

// Rate limiting
export const LOGIN_RATE_LIMIT = 5; // attempts
export const LOGIN_RATE_WINDOW_MS = 15 * 60 * 1000; // 15 minutes

// Default times
export const DEFAULT_CHECKIN_TIME = '12:00';
export const DEFAULT_CHECKOUT_TIME = '12:00';

// Default property name
export const DEFAULT_PROPERTY_NAME = 'Apartment 5H Calendar';

// Friends (Guest) user constants
export const FRIENDS_USER_ID = 'friends-system-user';
export const FRIENDS_USER_EMAIL = 'friends@system.local';
export const FRIENDS_USER_NAME = 'Friends';

// Default timezone
export const DEFAULT_TIMEZONE = 'America/New_York';

// Booking notes max length
export const BOOKING_NOTES_MAX_LENGTH = 250;

// File upload limits
export const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10MB

// Allowed file types
export const ALLOWED_FILE_TYPES = {
  images: {
    extensions: ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
    mimeTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  },
  documents: {
    extensions: ['.pdf'],
    mimeTypes: ['application/pdf'],
  },
  word: {
    extensions: ['.doc', '.docx'],
    mimeTypes: [
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ],
  },
  text: {
    extensions: ['.txt'],
    mimeTypes: ['text/plain'],
  },
} as const;

// Password requirements
export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MAX_LENGTH = 128;
export const PASSWORD_REQUIREMENTS = {
  minLength: PASSWORD_MIN_LENGTH,
  maxLength: PASSWORD_MAX_LENGTH,
  requireUppercase: true,
  requireLowercase: true,
  requireNumber: true,
  requireSymbol: true,
};

// Audit log retention
export const AUDIT_LOG_RETENTION_MONTHS = 12;

// API error codes
export const ERROR_CODES = {
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  PASSWORD_REQUIREMENTS: 'PASSWORD_REQUIREMENTS',
  UNAUTHORIZED: 'UNAUTHORIZED',
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  FORBIDDEN: 'FORBIDDEN',
  BOOKING_REQUIRED: 'BOOKING_REQUIRED',
  NOT_FOUND: 'NOT_FOUND',
  DATE_CONFLICT: 'DATE_CONFLICT',
  EMAIL_EXISTS: 'EMAIL_EXISTS',
  RATE_LIMITED: 'RATE_LIMITED',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  HAS_BOOKING_HISTORY: 'HAS_BOOKING_HISTORY',
} as const;

// Audit action types
export const AUDIT_ACTIONS = {
  USER_CREATED: 'user.created',
  USER_UPDATED: 'user.updated',
  USER_DEACTIVATED: 'user.deactivated',
  USER_REACTIVATED: 'user.reactivated',
  USER_DELETED: 'user.deleted',
  ADMIN_PASSWORD_RESET: 'user.admin_password_reset',
  USER_LOGIN: 'user.login',
  USER_LOGOUT: 'user.logout',
  BOOKING_REQUESTED: 'booking.requested',
  BOOKING_APPROVED: 'booking.approved',
  BOOKING_DECLINED: 'booking.declined',
  BOOKING_CANCELLED: 'booking.cancelled',
  BOOKING_MODIFIED: 'booking.modified',
  BOOKING_REASSIGNED: 'booking.reassigned',
  BOOKING_DELETED: 'booking.deleted',
  SETTINGS_UPDATED: 'settings.updated',
  PROPERTY_UPDATED: 'property.updated',
  CALENDAR_IMPORTED: 'calendar.imported',
  GUIDE_CREATED: 'guide.created',
  GUIDE_UPDATED: 'guide.updated',
  GUIDE_DELETED: 'guide.deleted',
  DOCUMENT_UPLOADED: 'document.uploaded',
  DOCUMENT_DELETED: 'document.deleted',
  INVITATION_SENT: 'invitation.sent',
  INVITATION_ACCEPTED: 'invitation.accepted',
  PASSWORD_RESET_REQUESTED: 'password.reset_requested',
  PASSWORD_RESET_COMPLETED: 'password.reset_completed',
  REMINDER_SENT: 'reminder.sent',
  REMINDER_UNSUBSCRIBED: 'reminder.unsubscribed',
  ANNOUNCEMENT_SENT: 'announcement.sent',
} as const;
